import { Injectable, Injector } from '@angular/core';
import { lastValueFrom, Observable, ReplaySubject } from 'rxjs';
import { ErrorType } from '../enums/enums.enum';
import { ErrorModel } from '../models/error-model';
import { RestService } from '@copec-gid/gid-arquitectura.seguridad-autorizacion-modulo';
import { GidLoggerService } from '@copec-gid/gid-arquitectura.console-log';
import { Globals } from '../globals';
import { TokenizationService } from './tokenization.service';
import { EndpointResource } from '../core/conf/endpoint_conf';
import { LIB_HEADERS } from '../core/conf/lib_conf';

@Injectable({
  providedIn: 'root'
})
export class CustomErrorHandler {

  constructor(
    private logger: GidLoggerService,
    private injector: Injector
  ) { }

  // Comúnmente se utiliza BehaviorSubject para el patrón de Observable Data Services. 
  // Acá usamos ReplaySubject dado que, con su valor default de 1, es exactamente igual
  // a BehaviorSubject (emisión del último valor para todos los subscribers), sólo que 
  // sin valor inicial de emisión (típicamente null para BehaviorSubject), que no necesitamos.
  // * Diferencia: "if you subscribe to a completed BehaviorSubject, you won’t receive 
  // the last value but for a ReplaySubject(1) you will receive the last value"
  private _errors: ReplaySubject<ErrorModel> = new ReplaySubject<ErrorModel>();
  public readonly errors$: Observable<ErrorModel> = this._errors.asObservable();

  private _errorLog: ErrorModel[] = [];

  // Violaciones CSP ya reportadas ("directiva origen"): una misma violación dispara el
  // evento muchas veces, y el POST del log podría a su vez violar la CSP y entrar en loop
  private static readonly MAX_CSP_REPORTS = 10;
  private static readonly CSP_KEYWORDS = new Set(['inline', 'eval', 'wasm-eval', 'data', 'blob']);
  private static readonly EXTENSION_URI = /^(chrome|moz|safari-web|ms-browser)-extension:/;
  private reportedCspViolations = new Set<string>();
  private listeningCspViolations = false;
  

  private buildTimeoutErrorPayload(): any {
    return {
      status: 504,
      error: {
        status_code: 'TIMEOUT',
        user_message_title: 'Ha ocurrido un error',
        user_message_desc: 'No se pudo completar la operación. Por favor, intente nuevamente más tarde.',
      },
    };
  }

  private buildNullishErrorPayload(): any {
    return {
      status: 200,
      error: {
        status_code: 'NULLISH',
        user_message_title: 'Ha ocurrido un error',
        user_message_desc: 'No se pudo completar la operación. Por favor, intente nuevamente más tarde.',
      },
    };
  }

  private buildDataConvertErrorPayload(): any {
    return {
      status: 200,
      error: {
        status_code: 'DATA_CONVERT',
        user_message_title: 'Ha ocurrido un error',
        user_message_desc: 'No se pudo completar la operación. Por favor, intente nuevamente más tarde.',
      },
    };
  }

  private buildStatus0ErrorPayload(): any {
    return {
      status: 200,
      error: {
        status_code: 'STATUS_0',
        user_message_title: 'Ha ocurrido un error',
        user_message_desc: 'No se pudo completar la operación. Por favor, intente nuevamente más tarde.',
      },
    };
  }

  private buildCspViolationErrorPayload(): any {
    return {
      status: 200,
      error: {
        status_code: 'CSP_VIOLATION',
        user_message_title: 'Ha ocurrido un error',
        user_message_desc: 'No se pudo completar la operación. Por favor, intente nuevamente más tarde.',
      },
    };
  }

  private buildDataCollectionErrorPayload(): any {
    return {
      status: 504,
      error: {
        status_code: 'DATA_COLLECTION_TIMEOUT',
        user_message_title: 'Ha ocurrido un error',
        user_message_desc: 'No se pudo completar la operación. Por favor, intente nuevamente más tarde.',
      },
    };
  }

  reportError(error: Error | any, errorType: ErrorType): void {
    if (errorType === ErrorType.TIMEOUT) {
      error = this.buildTimeoutErrorPayload();
    }

    if (errorType === ErrorType.DATA_CONVERT) {
      error = this.buildDataConvertErrorPayload();
    }

    if (errorType === ErrorType.STATUS_0) {
      error = this.buildStatus0ErrorPayload();
    }

    if (errorType === ErrorType.DATA_COLLECTION_TIMEOUT) {
      error = this.buildDataCollectionErrorPayload();
    }

    if (!error) {
      error = this.buildNullishErrorPayload();
    }
    const errorModel = new ErrorModel(error, errorType);
    this._errorLog.push(errorModel);
    this._errors.next(errorModel);
    this.logError(errorModel);
  }

  getErrorLog(): ErrorModel[] {
    return this._errorLog;
  }

  // Optional
  clearErrorLog(): void {
    this._errorLog = [];
  }

  isTimeoutError(error: any): boolean {
    return error?.name === 'TimeoutError';
  }

  listenToCspViolations(): void {
    if (this.listeningCspViolations) return;
    this.listeningCspViolations = true;
    document.addEventListener('securitypolicyviolation', (event) => this.reportCspViolation(event));
  }

  /*
    Sólo registra la directiva y el origen bloqueado: la URL completa puede traer
    tokens en la query. No emite en errors$ (no corta el flujo del usuario) y descarta
    lo que provenga de extensiones del navegador (ruido y dato de fingerprinting).
  */
  reportCspViolation(event: SecurityPolicyViolationEvent): void {
    if (CustomErrorHandler.EXTENSION_URI.test(event.sourceFile ?? '')) return;

    const directive = event.effectiveDirective;
    if (!/^[a-z-]{1,40}$/.test(directive ?? '')) return;

    const blocked = CustomErrorHandler.sanitizeBlockedUri(event.blockedURI);
    if (!blocked) return;

    const key = `${directive} ${blocked}`;
    if (this.reportedCspViolations.has(key)) return;
    if (this.reportedCspViolations.size >= CustomErrorHandler.MAX_CSP_REPORTS) return;
    this.reportedCspViolations.add(key);

    const errorModel = new ErrorModel(this.buildCspViolationErrorPayload(), ErrorType.CSP_VIOLATION);
    this.logError(errorModel, key);
  }

  private static sanitizeBlockedUri(uri: string): string | null {
    if (CustomErrorHandler.CSP_KEYWORDS.has(uri)) return uri;
    try {
      const url = new URL(uri);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      return url.origin.length <= 255 ? url.origin : null;
    } catch {
      return null;
    }
  }

  private async logError(error: ErrorModel, technicalMessage = error.getMessageString()): Promise<void> {
    let logErrorResponse: any;
    try {
      /* Vía injector, igual que TokenizationService, para no cerrar el ciclo de DI */
      logErrorResponse = await lastValueFrom(this.injector.get(RestService).post<any>(
        EndpointResource.errorLogEndpoint,
        {
          "code": "500",
          "status_code_api": error.statusCode,
          "user_message": error.type,
          "technical_message": technicalMessage,
          "mediopagoId": this.injector.get(TokenizationService).medioPagoId,
          "servicio": ErrorType.toEndpoint(error.type),
        },
        { headers: LIB_HEADERS })
      );
    } catch (error) {
      this.logger.error('logError', Globals.sanitizeForLog(error));
      return;
    }

    if (!logErrorResponse) {
      this.logger.error('logError empty data', Globals.sanitizeForLog(logErrorResponse));
      return;
    }

    if (logErrorResponse.status && logErrorResponse.status !== 200) {
      this.logger.error('logError status not 200', Globals.sanitizeForLog(logErrorResponse));
      return;
    }

    if (logErrorResponse.status_code && logErrorResponse.status_code !== 'S00') {
      this.logger.error('logError status_code not S00', Globals.sanitizeForLog(logErrorResponse));
      return;
    }

  }


}